import bearerAxios from '../../helpers/bearerAxios';
import { skipPartnerErrorPopupConfig } from '../../helpers/partnerApiErrorInterceptor';
import { logger } from '../loggingService';

const stripSlash = (value: string): string => value.replace(/\/$/, '');

/** V1 `environment.contractApiUrl`. Must be set per env; no host fallback. */
const v1ContractApiUrl = (): string => {
    const url = stripSlash(String(import.meta.env.VITE_ONEBUTTON_CONTRACT_V1_API_URL || '').trim());
    if (!url) throw new Error('VITE_ONEBUTTON_CONTRACT_V1_API_URL is not configured');
    return url;
};

const stripeRoot = (): string => {
    const raw = String(import.meta.env.VITE_ONEPAYMENT_STRIPE_URL || '').trim();
    if (!raw) return '';
    return raw.endsWith('/') ? raw : `${raw}/`;
};

export const getOneButtonAppId = (): number => {
    const fromEnv = Number(import.meta.env.VITE_ONEBUTTON_APP_ID);
    if (Number.isFinite(fromEnv) && fromEnv > 0) return fromEnv;
    const capUrl = String(import.meta.env.VITE_ONEBUTTON_GET_USER_CAPABILITY_URL || '');
    const tail = Number(capUrl.split('/').filter(Boolean).pop());
    if (Number.isFinite(tail) && tail > 0) return tail;
    return 59;
};

/** onePAYMENT Stripe now accepts the OneDraw login token (Dev payment-gateway change). */
const onePaymentRequest = async (method: 'get' | 'post', url: string, body?: unknown): Promise<unknown> => {
    const response =
        method === 'get'
            ? await bearerAxios.get(url, skipPartnerErrorPopupConfig)
            : await bearerAxios.post(url, body, skipPartnerErrorPopupConfig);
    return response?.data;
};

export type StripeContractAmount = {
    ProductName: string;
    OpportunityContractId?: number;
    Contract_Amount?: number;
};

export type StripePaymentRow = {
    paid_Amount?: number | null;
    payment_Date?: string | null;
    payment_Status?: string | null;
};

export type StripePaymentLinkRow = {
    payment_Link?: string;
    date_Generated?: string | null;
    created_By?: string | null;
    opportunity_Product?: string | null;
    paymentType?: string | null;
    total_Amount?: number | null;
    amount_Paid?: number | null;
    balance_Amount?: number | null;
    link_Status?: string | null;
    payments?: StripePaymentRow[];
};

export type StripeAmountSplitUp = {
    product?: string;
    amount?: number | null;
    invoices?: number | null;
    payments?: number | null;
    balanceRemaining?: number | null;
    OpportunityContractId?: number;
    Contract_Amount?: number | null;
};

export type StripeAmountDetails = {
    totalContractAmount?: number | null;
    totalAmount?: number | null;
    amountSplitUps?: StripeAmountSplitUp[];
};

export type StripePaymentsLinksPayload = {
    payment_Link_Details: StripePaymentLinkRow[];
    amountDetails: StripeAmountDetails;
};

export type StripePaymentType = {
    payment_Type_Name?: string;
    Payment_Type_Name?: string;
};

export type CreateStripePaymentLinkPayload = Record<string, unknown>;

const parseMaybeJson = (value: unknown): unknown => {
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const unwrapData = (payload: unknown): unknown => {
    const parsed = parseMaybeJson(payload);
    if (parsed && typeof parsed === 'object' && parsed !== null && 'data' in (parsed as object)) {
        return parseMaybeJson((parsed as { data: unknown }).data);
    }
    return parsed;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

export const summarizeStripeLinkStatus = (
    links: StripePaymentLinkRow[] | null | undefined,
): { sublabel: string; complete: boolean; badgeTone: 'paid' | 'partial' | 'unpaid' } => {
    const rows = Array.isArray(links) ? links : [];
    const isPaid = (row: StripePaymentLinkRow): boolean => String(row.link_Status || '').toLowerCase() === 'paid';
    if (rows.length === 0) return { sublabel: 'No payments', complete: false, badgeTone: 'unpaid' };
    const paidCount = rows.filter(isPaid).length;
    if (paidCount === rows.length) return { sublabel: 'Paid', complete: true, badgeTone: 'paid' };
    if (paidCount > 0) return { sublabel: 'Pending', complete: false, badgeTone: 'partial' };
    return { sublabel: 'Pending', complete: false, badgeTone: 'unpaid' };
};

/** V1 `opportunityInfoService.StripeContractAmount` → Azure `GET api/Contract/StripeContractAmount`. */
const getStripeContractAmount = async (opportunityId: string | number): Promise<StripeContractAmount[]> => {
    try {
        const url = `${v1ContractApiUrl()}/StripeContractAmount?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await bearerAxios.get(url, skipPartnerErrorPopupConfig);
        const data = unwrapData(response?.data);
        return Array.isArray(data) ? (data as StripeContractAmount[]) : [];
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/**
 * V1 `stripe-model` `GetStripePaymentsLinks`.
 * onePAYMENT `GET payment-link?contractId=&opportunityId=&salesforceOpportunityId=`
 * plus `&amountSource=contract` when appName is oneBUTTON.
 */
const getStripePaymentsLinks = async (
    contractId: string | number,
    opportunityId: string | number,
    salesforceOpportunityId: string,
    appName: string,
): Promise<StripePaymentsLinksPayload> => {
    try {
        const root = stripeRoot();
        if (!root) throw new Error('VITE_ONEPAYMENT_STRIPE_URL is not configured');
        let url = `${root}payment-link?contractId=${encodeURIComponent(String(contractId))}&opportunityId=${encodeURIComponent(String(opportunityId))}&salesforceOpportunityId=${encodeURIComponent(salesforceOpportunityId)}`;
        if (appName === 'oneBUTTON') url += '&amountSource=contract';
        const data = asRecord(unwrapData(await onePaymentRequest('get', url))) ?? {};
        const links = data.payment_Link_Details ?? data.Payment_Link_Details;
        const amountDetails = (data.amountDetails ?? data.AmountDetails ?? {}) as StripeAmountDetails;
        return {
            payment_Link_Details: Array.isArray(links) ? (links as StripePaymentLinkRow[]) : [],
            amountDetails: amountDetails ?? {},
        };
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** V1 `getStripePaymentType` → onePAYMENT `GET payment-type`. */
const getStripePaymentType = async (): Promise<StripePaymentType[]> => {
    try {
        const root = stripeRoot();
        if (!root) throw new Error('VITE_ONEPAYMENT_STRIPE_URL is not configured');
        const data = unwrapData(await onePaymentRequest('get', `${root}payment-type`));
        return Array.isArray(data) ? (data as StripePaymentType[]) : [];
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** V1 `createNewPayment` → onePAYMENT `POST payment-link`. Returns the generated URL string. */
const createNewPayment = async (payload: CreateStripePaymentLinkPayload): Promise<string> => {
    try {
        const root = stripeRoot();
        if (!root) throw new Error('VITE_ONEPAYMENT_STRIPE_URL is not configured');
        const data = unwrapData(await onePaymentRequest('post', `${root}payment-link`, payload));
        if (typeof data === 'string') return data;
        if (data && typeof data === 'object' && 'link' in (data as object)) {
            return String((data as { link?: unknown }).link ?? '');
        }
        return data != null ? String(data) : '';
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

const stripeServices = {
    getStripeContractAmount,
    getStripePaymentsLinks,
    getStripePaymentType,
    createNewPayment,
};

export default stripeServices;
